import React, { useEffect, useState } from 'react';
import { getRegistrationOptions, registerUser } from '../api';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  dateOfBirth: '',
  gender: '',
  weight: '',
  race: '',
};

const EMPTY_CONSENT = {
  consentAge: false,
  consentTerms: false,
  consentElectronic: false,
  consentInsights: false,
};

function Register({ onGoToLogin, onRegistered }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const [consentMeta, setConsentMeta] = useState(null);
  const [options, setOptions] = useState({ genders: [], races: [], consent: null });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getRegistrationOptions()
      .then((data) => {
        setOptions(data);
        setConsentMeta(data.consent || null);
      })
      .catch(() => {
        setOptions({
          genders: ['Female', 'Male', 'Non-binary', 'Prefer not to say', 'Other'],
          races: [
            'American Indian or Alaska Native',
            'Asian',
            'Black or African American',
            'Hispanic or Latino',
            'Native Hawaiian or Other Pacific Islander',
            'White',
            'Two or more races',
            'Prefer not to say',
            'Other',
          ],
          consent: null,
        });
      });
  }, []);

  const updateField = (field) => (e) => {
    setForm((current) => ({ ...current, [field]: e.target.value }));
  };

  const toggleConsent = (field) => (e) => {
    setConsent((current) => ({ ...current, [field]: e.target.checked }));
  };

  const requiredComplete =
    consent.consentAge && consent.consentTerms && consent.consentElectronic;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!requiredComplete) {
      setError('Confirm all required consent items to create an account.');
      return;
    }

    if (!consentMeta?.version) {
      setError('Consent options did not load. Refresh the page and try again.');
      return;
    }

    setLoading(true);

    try {
      const result = await registerUser({
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        weight: form.weight.trim(),
        consentVersion: consentMeta.version,
        ...consent,
      });
      if (result.success) {
        setForm(EMPTY_FORM);
        setConsent(EMPTY_CONSENT);
        const text =
          result.message ||
          'Check your email for a confirmation link to activate your account.';
        if (onRegistered) {
          onRegistered(text);
          return;
        }
        sessionStorage.setItem('mwbNotice', text);
        window.location.assign('/login');
        return;
      }
      setError(result.error || 'Registration failed');
    } catch (err) {
      setError(err.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container wide">
      <p className="brand">My Weed Buddy</p>
      <h1>Create an account</h1>
      <p className="subtitle">
        Required fields are marked with *. After you register, we will email a confirmation
        link to activate your account.
      </p>

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              id="firstName"
              value={form.firstName}
              onChange={updateField('firstName')}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              id="lastName"
              value={form.lastName}
              onChange={updateField('lastName')}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            value={form.email}
            onChange={updateField('email')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            value={form.password}
            onChange={updateField('password')}
            required
            minLength={8}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dateOfBirth">Date Of Birth *</label>
          <input
            type="date"
            id="dateOfBirth"
            value={form.dateOfBirth}
            onChange={updateField('dateOfBirth')}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">
              Gender <span className="optional">(optional)</span>
            </label>
            <select id="gender" value={form.gender} onChange={updateField('gender')}>
              <option value="">Select</option>
              {(options.genders || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="weight">
              Weight <span className="optional">(optional, lbs)</span>
            </label>
            <input
              id="weight"
              type="number"
              min="1"
              step="0.1"
              value={form.weight}
              onChange={updateField('weight')}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="race">
            Race <span className="optional">(optional)</span>
          </label>
          <select id="race" value={form.race} onChange={updateField('race')}>
            <option value="">Select</option>
            {(options.races || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {consentMeta ? (
          <fieldset className="consent-section">
            <legend>Consent &amp; privacy *</legend>
            <p className="field-note">
              Required for all users. These choices help us meet privacy expectations across
              U.S. states. We never sell your name, email, or contact information.
            </p>
            <ul className="consent-list">
              {(consentMeta.required || []).map((item) => (
                <li key={item.id} className="consent-item">
                  <label htmlFor={item.field}>
                    <input
                      id={item.field}
                      type="checkbox"
                      checked={Boolean(consent[item.field])}
                      onChange={toggleConsent(item.field)}
                    />
                    <span>{item.label}</span>
                  </label>
                </li>
              ))}
            </ul>
            {(consentMeta.optional || []).length ? (
              <>
                <p className="field-note consent-optional-heading">Optional</p>
                <ul className="consent-list consent-list-optional">
                  {(consentMeta.optional || []).map((item) => (
                    <li key={item.id} className="consent-item">
                      <label htmlFor={item.field}>
                        <input
                          id={item.field}
                          type="checkbox"
                          checked={Boolean(consent[item.field])}
                          onChange={toggleConsent(item.field)}
                        />
                        <span>{item.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </fieldset>
        ) : (
          <p className="field-note">Loading consent options…</p>
        )}

        <button type="submit" disabled={loading || !consentMeta || !requiredComplete}>
          {loading ? 'Creating account...' : 'Register'}
        </button>
      </form>

      <p className="footer-link">
        Already have an account?{' '}
        <button type="button" onClick={onGoToLogin}>
          Sign in
        </button>
      </p>
    </div>
  );
}

export default Register;
